import {Injectable, Optional} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {Pagination, Product, ProductFacet, ProductSearch, ProductSort} from '@kion/data-models';
import {HttpClient} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';
import { CmsConfig } from '../../../../../../lmh/cms/src/lib/config/cms.config';

interface IResponseItem {
  code: string,
  name: string,
  summary: string,
  url: string,
  deliveryTime: number,
  deliveryTimeInDays: number,
  categories: ICategory[],
  applications: string,
  availableForPickup: boolean,
  configurable: boolean,
  description: string,
  numberOfReviews: number,
  purchasable: boolean,
  isOrderable: boolean,
  isRequestable:boolean,
  stock: {
    stockLevel: number,
    stockLevelStatus: string
  },
  usps: string,
  baseProduct: string,
  price: IPrice,
  variantOptions: IVariant[],
  images: IImages[],
  classifications: any,
  customerMaterialNumber?: string[],
  unit?: string
}

interface IImages {
  url: string
}

interface ICategory {
  code: string,
  name: string
  isVariant: boolean,
  sortOrder: number,
  url: string
}
interface IPrice {
    currencyIso: string,
    formattedValue: string,
    priceType: string,
    value: number
 }

interface IVariant {
  code: string,
  stock: {
    stockLevel: number
  },
  url: string
}

interface SearchResponse {
  pagination: Pagination,
  products: IResponseItem[],
  facets: Facet[],
  sorts: Sort[]
}

interface Facet {
  category: boolean,
  multiSelect: boolean,
  code: string,
  name: string,
  priority: number,
  values: FacetValue[]
}

interface FacetValue {
  count: number,
  name: string,
  query: {
    query: {
      value: string
    },
    url: string
  },
  selected: boolean
}

interface Sort {
  code: string;
  selected: boolean;
}

interface IResponseReferences {
  target: IResponseItem
}

interface SearchResponseReferences {
  references: IResponseReferences[]
}

@Injectable({
  providedIn: 'root'
})
export class ProductConnectors {

  private readonly _rootUrl: string;
  private readonly _baseSite: string;
  private readonly _project: string;

  private route = '/products/';

  constructor(
    @Optional() apiConfig: ApiConfig,
    private http: HttpClient,
	private baseSiteConfig: CmsConfig
  ) {
    this._rootUrl = apiConfig.rootUrl;
    this._baseSite = apiConfig.baseSite;
	this._project = baseSiteConfig.project;
  }

  private static transformFromApi(response: IResponseItem, project: string): Product {
    if (this.isLocalhost() && response.images) {
      response = this.prependDevUrlToImageUrls(response);
    }
    let noPrice = false;
	let emptyPrice : IPrice = {currencyIso:'',formattedValue:'',priceType:'EMPTY',value:0};
	let dematic : boolean = project == 'Dematic';
	
    if (!response.price) {
      noPrice = true;
	  emptyPrice = null;
    }
    if (response.price) {
      if (response.price.value == 0) {
        noPrice = true;
      }
    }
    return {
      ...response,
      id: response.code,
      /*price: noPrice ? null : response.price*/
	  price: noPrice ? (dematic?emptyPrice:null) : response.price
    };
  }

  private static transformFacetFromApi(response: Facet): ProductFacet {
    return {
      ...response
    };
  }

  private static transformSortFromApi(response: Sort): ProductSort {
    return {
      ...response
    };
  }

  private static isLocalhost(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  private static prependDevUrlToImageUrls(response: IResponseItem): IResponseItem {
    response.images = response.images.filter((image: IImages) => image.url).map((image: IImages) => {
      return image;
    });

    return response;
  }

  search(
    query: string,
    page: number,
    pageSize: number,
    sort: string
  ): Observable<ProductSearch> {
    let searchOptions = 'currentPage=' + (page - 1) + '&pageSize=' + pageSize;
    if (query) {
      searchOptions = searchOptions + '&query=' + query;
    }
    if (sort) {
      searchOptions = searchOptions + '&sort=' + sort;
    }
    return this.http.get<SearchResponse>(
      this.getSearchUrl() + '?' + searchOptions
    ).pipe(
      map(
        (response: SearchResponse) => {
          return {
            pagination: response.pagination,
            products: response.products.map((product: IResponseItem) => ProductConnectors.transformFromApi(product, this._project)),
            facets: response.facets.map((facet: Facet) => ProductConnectors.transformFacetFromApi(facet)),
            sorts: response.sorts.map((sort: Sort) => ProductConnectors.transformSortFromApi(sort))
          }
        }
      ),
      catchError(error => throwError(error))
    )
  }

  getById(id: string): Observable<Product> {
    return this.http.get<IResponseItem>(this.getOneUrl(id)).pipe(
      map(
        (response: IResponseItem) => {
          return ProductConnectors.transformFromApi(response, this._project);
        }
      ),
      catchError(error => throwError(error))
    );
  }

  getByIds(ids: string[]): Observable<Product[]> {
    return this.http.post<SearchResponse>(this.getByIdsUrl() + '?fields=FULL', {productCodes: ids.map(id => id)}).pipe(
      map(
        (response: SearchResponse) => response.products.map(
          (result: IResponseItem) => ProductConnectors.transformFromApi(result, this._project)
        )
      ),
      catchError(error => throwError(error))
    );
  }

  getReferencesForProduct(productId: string, references: string[]): Observable<Product[]> {
    const route = this._rootUrl + this._baseSite + '/products/' + productId + '/references?pageSize=1000&referenceType=' + encodeURIComponent(references.join(','));
    return this.http.get<SearchResponseReferences>(route).pipe(
      map(
        (response: SearchResponseReferences) => response.references.map(result => ProductConnectors.transformFromApi(result.target, this._project))
      ),
      catchError(error => throwError(error))
    )
  }

  protected getManyUrl() {
    return this._rootUrl + this.route;
  }

  protected getOneUrl(id: string) {
    return this._rootUrl + this._baseSite + this.route + id;
  }

  protected getSearchUrl(): string {
    return this._rootUrl + this._baseSite + this.route + 'search';
  }

  protected getByIdsUrl(): string {
    return this._rootUrl + this._baseSite + this.route + 'idsearch';

  }
}
